<template>
  <div>

    <!-- Page Header (screen only) -->
    <div class="page-header no-print">
      <div>
        <h5 class="page-title">Sales Receipt</h5>
        <p class="page-sub">
          <span class="batch-pill">{{ sales?.batch_code }}</span>
        </p>
      </div>
      <div class="page-actions">
        <button type="button" @click="$router.go(-1)" class="btn-outline">
          <font-awesome-icon icon="arrow-left" /> Back
        </button>
        <PrintButtonHeader
          v-if="!loading"
          :title="'Invoice-' + sales?.batch_code"
          :urlA4="'/sales-receipt/'"
          :url58mm="'/print58mm-sales-receipt/'"
          :url80mm="'/print80mm-sales-receipt/'"
          :currentUrl="'Print 80mm Sales Receipt'"
          :data="sales"
          :type="'Sales Receipt'"
        />
        <button class="btn-primary-action no-print" onclick="window.print()">
          <font-awesome-icon icon="print" /> Print
        </button>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="receipt-skeleton no-print">
      <div class="skeleton skeleton--hero"></div>
      <div class="skeleton skeleton--line mt-2" v-for="n in 6" :key="n"></div>
    </div>

    <!-- Receipt -->
    <div v-else class="receipt-wrap">
      <div class="receipt" id="printMe">

        <!-- Header -->
        <div class="receipt-header">
          <p class="receipt-company">{{ company?.company_name }}</p>
          <p class="receipt-detail" v-if="sales?.store?.address">{{ sales.store.address }}</p>
          <p class="receipt-detail" v-if="sales?.store?.phone_no || company?.phone">
            {{ [sales?.store?.phone_no, company?.phone].filter(Boolean).join(', ') }}
          </p>
          <p class="receipt-detail" v-if="company?.company_email">{{ company.company_email }}</p>
        </div>

        <div class="receipt-dots"></div>

        <!-- Meta -->
        <div class="receipt-meta">
          <div class="receipt-meta-row">
            <span>Date</span>
            <span>{{ dateTime(sales?.date) }}, {{ timeConvert(sales?.time) }}</span>
          </div>
          <div class="receipt-meta-row">
            <span>Sales No</span>
            <span class="mono">{{ sales?.batch_code }}</span>
          </div>
          <div class="receipt-meta-row">
            <span>Store</span>
            <span>{{ sales?.store?.store_name }}</span>
          </div>
          <div class="receipt-meta-row" v-if="sales?.customer?.customer_name">
            <span>Customer</span>
            <span>{{ sales.customer.customer_name }}</span>
          </div>
          <div class="receipt-meta-row">
            <span>Sold By</span>
            <span>{{ sales?.user?.fullname }}</span>
          </div>
        </div>

        <div class="receipt-dots"></div>
        <div class="receipt-line"></div>

        <!-- Items -->
        <div class="receipt-items">
          <div v-for="item in sales_array" :key="item.product_name" class="receipt-item">
            <p class="receipt-item-name">{{ item?.product_name }}</p>
            <div v-if="item.variant" class="receipt-item-row">
              <span>
                {{ item.quantity }} {{ item.variant?.variant_name }}(s) × {{ $filters.formatMoney(item.variant?.variant_selling_price, company?.currency?.symbol) }}
              </span>
              <span class="fw-bold">{{ $filters.formatMoney(item.total, company?.currency?.symbol) }}</span>
            </div>
            <div v-else class="receipt-item-row">
              <span>
                {{ item.quantity }} {{ item.variant_name }} × {{ parseFloat(item.selling_price).toFixed(2) }}
              </span>
              <span class="fw-bold">{{ $filters.formatMoney(item.quantity * item.selling_price, company?.currency?.symbol) }}</span>
            </div>
            <div class="receipt-line mt-1"></div>
          </div>
        </div>

        <div class="receipt-dots"></div>

        <!-- Totals -->
        <div class="receipt-totals">
          <div class="receipt-totals-row">
            <span>Subtotal</span>
            <span>{{ $filters.formatMoney(subTotal, company?.currency?.symbol) }}</span>
          </div>
          <div class="receipt-totals-row" v-if="totalTax">
            <span>Tax{{ taxLabel }}</span>
            <span>{{ $filters.formatMoney(totalTax, company?.currency?.symbol) }}</span>
          </div>
          <div class="receipt-totals-row" v-if="totalDiscount">
            <span>Discount</span>
            <span>– {{ $filters.formatMoney(totalDiscount, company?.currency?.symbol) }}</span>
          </div>
          <div class="receipt-line"></div>
          <div class="receipt-totals-row receipt-totals-row--grand">
            <span>Total</span>
            <span>{{ $filters.formatMoney(sales?.total_amount, company?.currency?.symbol) }}</span>
          </div>
        </div>

        <div class="receipt-dots"></div>

        <!-- Payment -->
        <div class="receipt-payment">
          <div class="receipt-totals-row">
            <span>Payment</span>
            <span class="text-capitalize">{{ sales?.payment_type }}</span>
          </div>
          <div class="receipt-totals-row">
            <span>Amount Paid</span>
            <span>{{ $filters.formatMoney(sales?.amount_paid, company?.currency?.symbol) }}</span>
          </div>
          <div class="receipt-totals-row"
            v-if="sales?.payment_type === 'cash' || sales?.payment_type === 'card'">
            <span>Change</span>
            <span>{{ $filters.formatMoney(sales?.change, company?.currency?.symbol) }}</span>
          </div>
          <div class="receipt-totals-row">
            <span>Balance</span>
            <span>{{ $filters.formatMoney(sales?.balance ?? 0, company?.currency?.symbol) }}</span>
          </div>
        </div>

        <div class="receipt-dots"></div>

        <!-- Footer -->
        <div class="receipt-footer">
          <p><span v-html="poweredBy()"></span></p>
          <!-- <p class="receipt-footer-url">www.mapiibs.co.uk</p> -->
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import { reusableMixin } from '../../../lib/reusableMixin';
import { useStore } from 'vuex';
import axios from 'axios';

export default {
  mixins: [reusableMixin],

  data() {
    return {
      toast:       useToast(),
      sales:       null,
      sales_items: [],
      sales_array: [],
      store:       useStore(),
      loading:     true,
      company:     null,
    };
  },

  computed: {
    subTotal()     { return this.sales_array.reduce((a, b) => a + parseFloat(b.total    ?? 0), 0); },
    totalTax()     { return this.sales_array.reduce((a, b) => a + parseFloat(b.tax      ?? 0), 0); },
    totalDiscount(){ return this.sales_array.reduce((a, b) => a + parseFloat(b.discount ?? 0), 0); },
    uniqueTaxNames() {
      const names = new Set();
      this.sales_array.forEach(item => (item.tax_names ?? []).forEach(n => names.add(n)));
      return [...names];
    },
    taxLabel() {
      return this.uniqueTaxNames.length ? ` (${this.uniqueTaxNames.join(', ')})` : '';
    },
  },

  created() { this.getSale(); this.getCompany(); },

  methods: {
    getSale() {
      this.loading = true;
      axios.get(`/inventory/sales/${this.$route.params.id}`)
        .then(response => {
          this.loading     = false;
          this.sales       = response.data.data;
          this.sales_items = response.data.data.sales_item;
          this.sales_array = this.sales_items.map(s => ({
            product_name:  s?.product?.product_name,
            selling_price: s?.selling_price,
            variant_name:  s?.variant?.variant_name ?? s?.product?.uom?.name,
            variant:       s?.variant,
            quantity:      s?.quantity,
            tax:           parseFloat(s.tax_amount),
            tax_names:     (s?.product?.taxes ?? []).map(t => t.tax_name).filter(Boolean),
            discount:      parseFloat(s?.discount ?? 0),
            total:         parseFloat(s?.amount_paid),
          }));
        })
        .catch(() => {
          this.loading = false;
          this.toast.error('Something went wrong');
        });
    },
  },
};
</script>

<style scoped>
/* ── Page Header (screen) ── */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.page-sub     { margin:0; }
.page-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.batch-pill   { display:inline-block; background:#edf4ff; color:#004AAD; border:1px solid #c5d6f5; border-radius:20px; padding:2px 12px; font-size:13px; font-weight:600; }

/* ── Skeleton ── */
.receipt-skeleton { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; max-width:375px; margin:0 auto; }
.skeleton         { border-radius:6px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
.skeleton--hero { height:72px; }
.skeleton--line { height:18px; }
.mt-2 { margin-top:8px; }

/* ── Receipt Wrap (screen centering) ── */
.receipt-wrap { display:flex; justify-content:center; padding-bottom:32px; }

/* ── Receipt Shell ── */
.receipt {
  background:#fff;
  border:1px solid #e4eaf2;
  border-radius:12px;
  box-shadow:0 4px 24px rgba(0,0,0,.06);
  padding:24px 20px;
  width:375px;
  font-family:'Inter', sans-serif;
  font-size:13.5px;
  color:#35413B;
  line-height:1.7;
}

/* ── Header ── */
.receipt-header   { text-align:center; margin-bottom:12px; }
.receipt-company  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 4px; }
.receipt-detail   { font-size:12.5px; color:#4a5e78; margin:0; }

/* ── Dots / Line ── */
.receipt-dots {
  border:none;
  border-top:2px dashed #dde5f0;
  margin:12px 0;
}
.receipt-line {
  height:1px;
  background:#e4eaf2;
  margin:6px 0;
}

/* ── Meta ── */
.receipt-meta     { display:flex; flex-direction:column; gap:2px; }
.receipt-meta-row { display:flex; justify-content:space-between; font-size:13px; color:#4a5e78; }
.receipt-meta-row span:last-child { font-weight:600; color:#1a2b45; text-align:right; }
.mono { font-family:monospace; font-size:12.5px; }

/* ── Items ── */
.receipt-items   { display:flex; flex-direction:column; gap:4px; }
.receipt-item    { }
.receipt-item-name { font-weight:600; color:#1a2b45; margin:0 0 2px; }
.receipt-item-row  { display:flex; justify-content:space-between; font-size:13px; color:#4a5e78; }
.receipt-item-row .fw-bold { color:#1a2b45; }
.mt-1 { margin-top:4px; }

/* ── Totals ── */
.receipt-totals     { display:flex; flex-direction:column; gap:3px; }
.receipt-totals-row { display:flex; justify-content:space-between; font-size:13px; color:#4a5e78; }
.receipt-totals-row--grand { font-size:15px; font-weight:800; color:#1a2b45; margin-top:4px; }

/* ── Payment ── */
.receipt-payment    { display:flex; flex-direction:column; gap:3px; }
.text-capitalize    { text-transform:capitalize; }

/* ── Footer ── */
.receipt-footer     { text-align:center; font-size:12px; color:#aab8cc; }
.receipt-footer-url { font-size:11px; color:#c5d0dc; margin:0; }

/* ── Buttons ── */
.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
.btn-primary-action:hover { background:#003b8e; }
.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; }
.btn-outline:hover { background:#f4f7fb; }

/* ── Print ── */
@media print {
  .no-print  { display:none !important; }
  .receipt-wrap { padding:0; }
  .receipt   { border:none; border-radius:0; box-shadow:none; width:80mm; padding:8px; }
  body       { background:#fff; }
}

/* ── Responsive ── */
@media(max-width:576px) {
  .receipt   { width:100%; }
  .page-header,.page-actions { flex-direction:column; align-items:stretch; }
  .btn-outline,.btn-primary-action { width:100%; justify-content:center; }
}
</style>